import * as React from 'react';
import API from "../../common/API";
import { useAuth } from 'react-oidc-context';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';

interface IPriceGuideDates {
    effectiveDate: Date
    nextEffectiveDate: Date
    tier: string
    country: string
}

enum PriceSheetType {
    Controllers,
    Peripherals,
    ProtectorNet,
    Odyssey
}

enum PriceSheetStatus {
    Current,
    New
}

export function PricingComponent() {
    const auth = useAuth();
    const { t } = useTranslation()

    //#region States

    const [type, setType] = React.useState<PriceSheetType>(PriceSheetType.Controllers);
    const [status, setStatus] = React.useState<PriceSheetStatus>(PriceSheetStatus.Current)
    const [effectiveDate, setEffectiveDate] = React.useState<Date|null>(null);
    const [nextEffectiveDate, setNextEffectiveDate] = React.useState<Date | null>(null);
    const [tier, setTier] = React.useState<string>();
    const [country, setCountry] = React.useState<string>();
    const [error, setError] = React.useState<boolean>();

    //#endregion

    //#region Effects

    // Retrieves the current price guide dates, pricing tier and country
    React.useEffect(() => {
        const getPriceGuideDates = async () => {
            const res: IPriceGuideDates | null = await API.get("/api/v1/priceGuide", auth);
            if (res != null) {
                setEffectiveDate(new Date(res.effectiveDate));
                if (res.nextEffectiveDate) {
                    setNextEffectiveDate(new Date(res.nextEffectiveDate));
                    setStatus(PriceSheetStatus.New);
                }
                setTier(res.tier);
                setCountry(res.country);
            }
        }

        getPriceGuideDates();
    }, [auth])

    // Retrieves the Current/New price guide and stores in embed source
    React.useEffect(() => {
        const getPriceGuide = async (type: PriceSheetType) => {
            let headers = new Headers();
            headers.append('Authorization', 'Bearer ' + auth.user?.access_token)

            try {
                let res = await fetch("/api/v1/priceGuide/" + type.toString() + "/" + status.toString(), { headers })

                if (res.ok) {
                    let blob = await res.blob();
                    setError(false)
                    let embed = document.getElementById('pdf') as HTMLEmbedElement;
                    embed.setAttribute('src', window.URL.createObjectURL(blob));
                } else {
                    setError(true)
                }
            } catch (err) {
                console.log(err);
                setError(true)
            }
        }

        getPriceGuide(type);
    }, [auth, type, status])

    //#endregion

    //#region Event Handlers

    const download = async () => {
        let anchor = document.createElement('a');
        document.body.appendChild(anchor);

        let pdf = document.getElementById('pdf')

        if (pdf !== null) {
            anchor.href = pdf.getAttribute('src') ?? "";
            anchor.download = 'Hartmann Controls - ' + country + ' - ' + tier + ' - ' + effectiveDate?.toLocaleDateString() + ".pdf"
            anchor.click()
        }

        document.removeChild(anchor);
    }

    //#endregion

    //#region View
    return (
        <div className="h-100">
            <h1 className="mb-3">
                <FontAwesomeIcon icon="dollar-sign" className="me-2" />
                {t('pricing')}
            </h1>

            <div className="card mb-0">
                <div className="card-header p-0">
                    <ul className="nav nav-tabs">
                        <li className="nav-item">
                            <button className={"nav-link " + (type === PriceSheetType.Controllers ? "active" : "")} onClick={function () { setType(PriceSheetType.Controllers) }}>
                                {t('controllers')}
                            </button>
                        </li>
                        <li className="nav-item">
                            <button className={"nav-link " + (type === PriceSheetType.Peripherals ? "active" : "")} onClick={function () { setType(PriceSheetType.Peripherals) }}>
                                {t('peripherals')}
                            </button>
                        </li>
                        <li className="nav-item">
                            <button className={"nav-link " + (type === PriceSheetType.ProtectorNet ? "active" : "")} onClick={function () { setType(PriceSheetType.ProtectorNet) }}>
                                {t('protectorNet')}
                            </button>
                        </li>
                        <li className="nav-item">
                            <button className={"nav-link " + (type === PriceSheetType.Odyssey ? "active" : "")} onClick={function () { setType(PriceSheetType.Odyssey) }}>
                                {t('odyssey')}
                            </button>
                        </li>
                    </ul>
                </div>
                {nextEffectiveDate ?
                    <div className="card-header p-0">
                        <ul className="nav nav-tabs">
                            <li className="nav-item">
                                <button className={"nav-link " + (status === PriceSheetStatus.Current ? "active" : "")} onClick={function () { setStatus(PriceSheetStatus.Current) }}>
                                    {t('currentPricing')}
                                </button>
                            </li>
                            <li className="nav-item">
                                <button className={"nav-link " + (status === PriceSheetStatus.New ? "active" : "")} onClick={function () { setStatus(PriceSheetStatus.New) }}>
                                    {t('newPricing')}
                                </button>
                            </li>
                        </ul>
                    </div> : <></> }
                <div className="card-body" style={{ height: '70vh' }}>
                    <div className="row">
                        <dl className="col-sm-6 row">
                            <dt className="col-sm-2 fw-bolder">{t('effectiveDate')}</dt>
                            {status === PriceSheetStatus.Current ? < dd className="col-sm-9">{effectiveDate != null ? effectiveDate.toLocaleDateString() : ""}</dd> :
                                <dd className="col-sm-9">{nextEffectiveDate != null ? nextEffectiveDate.toLocaleDateString() : ""}</dd>}
                            <dt className="col-sm-2 fw-bolder">{t('priceTier')}</dt>
                            <dd className="col-sm-9">{tier}</dd>
                            <dt className="col-sm-2 fw-bolder">{t('country')}</dt>
                            <dd className="col-sm-9">{country}</dd>
                        </dl>
                        {!error ? <div className="col-6">
                            <button type="button" className="btn btn-primary" onClick={download}>{t('download')}</button>
                        </div> : <></>}
                    </div>
                    {!error ? <embed type="application/pdf" width="100%" style={{ height: "calc(100% - 100px)" }} id="pdf"></embed>
                        : <div className="text-center" style={{ fontWeight: 'bold' }}>An error occurred while trying to retrieve the price sheet. Contact us to receive pricing.</div>}
                </div>
            </div>

        </div>
    )
    //#endregion
}